import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useGeolocation } from '@myphx-engineering/location-utils';
import {
    Body1,
    Button,
    DefinitionList,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { constants } from '@myphx-engineering/phx-design-system/framework';
import { useIsMobile } from '@myphx-engineering/phx-design-system/hooks';
import {
    CashOfferPriceButton,
    InsuranceOfferPriceButton,
} from '@phx/myphx-lib';

import { getFragment } from '../../graphql';
import {
    type CashOfferInfoFragment,
    type InsuranceOfferInfoFragment,
    type ProviderOffersInfoFragment,
} from '../../graphql/generated/graphql';
import {
    isValidOfferPricing,
    isValidPatientCost,
} from '../../graphql/policies/util';
import { formatToLocalizedCurrency } from '../../util';
import { getCheapestPatientCostOffer } from '../../util/offers/get-cheapest-patient-cost-offer';
import { getOffersByProviderId } from '../../util/offers/get-offers-by-provider-id';
import { getPriceableOffers } from '../../util/offers/get-priceable-offers';
import {
    getPrimaryProvider,
    getProvider,
} from '../../util/provider-offers/provider-utils';
import { InfoHeading } from '../common/InfoHeading';
import { GenericSwapPricingDesc } from '../common/pricing/GenericSwapPricingDesc';
import {
    type TranslationBlocks,
    asDefinitionListItem,
} from '../utility/AsDefinitionListItem';

type OfferSelectionProps = {
    providerOffers: ProviderOffersInfoFragment;
    header: ReactNode;
    buttonText: string;
    onSelect: (selectedOfferId: string) => void;
};

export const OfferSelection = ({
    providerOffers,
    header,
    buttonText,
    onSelect,
}: OfferSelectionProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { geolocation } = useGeolocation();

    const generic = searchParams.get('generic');
    const genericName = searchParams.get('genericName');
    const brandName = searchParams.get('brandName');

    const providerId =
        searchParams.get('providerId') ??
        getPrimaryProvider(providerOffers, geolocation)?.id ??
        getFragment(providerOffers.provider)?.id;

    const offers = getOffersByProviderId(
        getPriceableOffers(providerOffers),
        providerId
    ) as Array<CashOfferInfoFragment | InsuranceOfferInfoFragment>;
    const isMobile = useIsMobile();

    const [selectedOffer, setSelectedOffer] = useState<
        CashOfferInfoFragment | InsuranceOfferInfoFragment | undefined
    >(getCheapestPatientCostOffer(offers));

    const pharmacy = getProvider(providerOffers, providerId);

    const dialogContentBlocks: TranslationBlocks = t<
        string,
        { returnObjects: true; tm: string },
        TranslationBlocks
    >('offerSelection.infoDialog.content', {
        returnObjects: true,
        tm: constants.unicode.TRADEMARK,
    });

    return (
        <Stack gap="lg" flex="1">
            <Stack gap="lg" flex={isMobile ? '1' : '0'}>
                <Stack gap="sm">
                    {!generic && header}
                    <InfoHeading
                        title={t('offerSelection.title')}
                        informationDialog={{
                            title: t('offerSelection.infoDialog.title'),
                            content: (
                                <DefinitionList
                                    compact={false}
                                    items={dialogContentBlocks.map(
                                        asDefinitionListItem
                                    )}
                                />
                            ),
                        }}
                    />
                    <Stack gap="xxs">
                        <Body1 inline>
                            <Body1 inline>{t('offerSelection.subtitle')}</Body1>
                            {pharmacy && (
                                <Body1 inline bold>
                                    {' '}
                                    {pharmacy.name}
                                </Body1>
                            )}
                        </Body1>
                        {generic && (
                            <GenericSwapPricingDesc
                                plural
                                brandName={brandName!}
                                genericName={genericName!}
                            />
                        )}
                    </Stack>
                </Stack>
                <Stack gap="sm">
                    {offers.map((offer, i) => {
                        if (isValidOfferPricing(offer)) {
                            const isSelected = offer.id === selectedOffer?.id;
                            const offerType = offer.__typename;

                            const price = isValidPatientCost(offer)
                                ? formatToLocalizedCurrency(
                                      offer.patientResponsibilityCost
                                  )
                                : undefined;

                            if (offerType === 'InsuranceOffer') {
                                const planPays = formatToLocalizedCurrency(
                                    offer.planPaysAmount
                                );
                                return (
                                    <InsuranceOfferPriceButton
                                        key={`offer-${offer.id}-insurance-${i}`}
                                        price={price}
                                        planPays={planPays}
                                        isSelected={isSelected}
                                        onClick={() => setSelectedOffer(offer)}
                                    />
                                );
                            } else if (offerType === 'CashOffer') {
                                return (
                                    <CashOfferPriceButton
                                        key={`offer-${offer.id}-cash-${i}`}
                                        price={price}
                                        isSelected={isSelected}
                                        onClick={() => setSelectedOffer(offer)}
                                    />
                                );
                            }
                        }

                        return null;
                    })}
                </Stack>
            </Stack>
            <Button
                size="lg"
                disabled={!selectedOffer}
                onClick={() => selectedOffer && onSelect(selectedOffer.id)}
            >
                {buttonText}{' '}
                {isValidPatientCost(selectedOffer)
                    ? formatToLocalizedCurrency(
                          selectedOffer?.patientResponsibilityCost ?? 0
                      )
                    : undefined}
            </Button>
        </Stack>
    );
};
